// @flow
import React, { type ChildrenArray, Component, type Element } from 'react';
import { ArrowKeyStepper, Column, Table } from 'react-virtualized';
import { findDOMNode } from 'react-dom';

import {
  computeNewSelection,
  type Item,
  getIndexOfFirstSelectedItem,
  isItemAtIndexSelected,
  type SelectionState,
  TABLE_HEADER_STYLE,
} from 'util/tableUtils';

type Props = {
  children: ChildrenArray<Element<typeof Column>>,
  headerHeight: number,
  height: number,
  onSelectionChanged: SelectionState => void,
  rowHeight: number | (({ index: number }) => number),
  selectionState: SelectionState,
  singleSelectionOnly: boolean,
  sortedItems: Item[],
  width: number,
};

export default class MultipleSelectionTable extends Component<Props> {
  handleScrollToChange: Function;
  onRowClick: Function;
  rowClassName: Function;
  rowGetter: Function;
  tableRef: ?typeof Table;

  static defaultProps = { singleSelectionOnly: false };

  constructor() {
    super();

    this.handleScrollToChange = this.handleScrollToChange.bind(this);
    this.onRowClick = this.onRowClick.bind(this);
    this.rowClassName = this.rowClassName.bind(this);
    this.rowGetter = this.rowGetter.bind(this);
  }

  onRowClick({
    event,
    index,
    rowData,
  }: {
    event: SyntheticEvent<>,
    index: number,
    rowData: {},
  }) {
    const {
      onSelectionChanged,
      sortedItems,
      selectionState,
      singleSelectionOnly,
    } = this.props;
    onSelectionChanged(
      computeNewSelection(
        index,
        sortedItems,
        selectionState,
        event,
        singleSelectionOnly
      )
    );
    this.focusTable();
  }

  handleScrollToChange({ scrollToRow }: { scrollToRow: number }) {
    const {
      onSelectionChanged,
      sortedItems,
      selectionState,
      singleSelectionOnly,
    } = this.props;
    onSelectionChanged(
      computeNewSelection(
        scrollToRow,
        sortedItems,
        selectionState,
        {},
        singleSelectionOnly
      )
    );
    this.focusTable();
  }

  focusTable() {
    if (this.tableRef) {
      const gridElement = findDOMNode(this.tableRef.Grid);
      gridElement instanceof HTMLElement && gridElement.focus();
    }
  }

  rowGetter({ index }: { index: number }) {
    return this.props.sortedItems[index];
  }

  rowClassName({ index }: { index: number }) {
    const { selectionState } = this.props;
    return `${index % 2 === 0 ? 'pseudo-table-row-striped' : ''} ${
      index < 0 ? 'pseudo-table-header' : 'pseudo-table-row'
    } ${
      isItemAtIndexSelected(index, selectionState)
        ? 'pseudo-table-row-selected'
        : ''
    } pseudo-table`;
  }

  render() {
    const {
      children,
      headerHeight,
      height,
      rowHeight,
      selectionState,
      singleSelectionOnly,
      sortedItems,
      width,
      ...rest
    } = this.props;

    const scrollToRow = getIndexOfFirstSelectedItem(selectionState);

    return (
      <ArrowKeyStepper
        columnCount={1}
        mode="cells"
        rowCount={sortedItems.length}
        onScrollToChange={this.handleScrollToChange}
        scrollToRow={scrollToRow}
      >
        {({ onSectionRendered, scrollToRow }) => (
          <Table
            className="hideFocus"
            headerHeight={headerHeight}
            headerStyle={TABLE_HEADER_STYLE}
            height={height}
            onRowClick={this.onRowClick}
            onSectionRendered={onSectionRendered}
            ref={ref => (this.tableRef = ref)}
            rowClassName={this.rowClassName}
            rowCount={sortedItems.length}
            rowGetter={this.rowGetter}
            rowHeight={rowHeight}
            scrollToIndex={scrollToRow}
            width={width}
            {...rest}
          >
            {children}
          </Table>
        )}
      </ArrowKeyStepper>
    );
  }
}



// WEBPACK FOOTER //
// ./src/view/App/MultipleSelectionTable.js